import { LitElement, html } from '@hh/global.js'
import {
  getStickyHeaderOffsetPx,
  isCollectionFiltersOrSortApplied,
  isScrollTargetElementVisible,
} from './component-anchor-target-utils.js'

class PlpAnchorTargetAtelier extends LitElement {
  static ensureLocationChangeEvents() {
    if (window.__plpAnchorLocationChangePatched) {
      return
    }
    window.__plpAnchorLocationChangePatched = true

    const dispatch = () => window.dispatchEvent(new Event('locationchange'))
    const rawPushState = history.pushState
    const rawReplaceState = history.replaceState

    history.pushState = function (...args) {
      const ret = rawPushState.apply(this, args)
      dispatch()
      return ret
    }
    history.replaceState = function (...args) {
      const ret = rawReplaceState.apply(this, args)
      dispatch()
      return ret
    }
    window.addEventListener('popstate', dispatch)
  }

  constructor() {
    super()
    this.progressRaf = null
    this.spyRaf = null
    this.lastActiveLink = null

    this.onRootClick = this.onRootClick.bind(this)
    this.onWindowScroll = this.onWindowScroll.bind(this)
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onLocationChange = this.onLocationChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    PlpAnchorTargetAtelier.ensureLocationChangeEvents()
    this.addEventListener('click', this.onRootClick)
    window.addEventListener('scroll', this.onWindowScroll, { passive: true })
    window.addEventListener('resize', this.onWindowResize, { passive: true })
    window.addEventListener('locationchange', this.onLocationChange)
    this.syncSectionVisibilityFromUrl()
    this.updateProgressRing()
    this.updateActiveChip()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.onRootClick)
    window.removeEventListener('scroll', this.onWindowScroll)
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('locationchange', this.onLocationChange)
    if (this.progressRaf != null) {
      cancelAnimationFrame(this.progressRaf)
      this.progressRaf = null
    }
    if (this.spyRaf != null) {
      cancelAnimationFrame(this.spyRaf)
      this.spyRaf = null
    }
  }

  onRootClick(event) {
    const topButton = event.target?.closest?.('[data-plp-anchor-go-to-top]')
    if (topButton && this.contains(topButton)) {
      event.preventDefault()
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
      return
    }

    const chipLink = event.target?.closest?.(
      'a[data-plp-anchor-chip-link][data-plp-scroll-target-product-id]',
    )
    if (!(chipLink instanceof HTMLAnchorElement) || !this.contains(chipLink)) {
      return
    }
    const target = this.resolveChipTarget(chipLink)
    if (!target) {
      return
    }
    event.preventDefault()
    this.scrollToChipTarget(chipLink)
  }

  onWindowScroll() {
    this.scheduleProgressRingUpdate()
    this.scheduleActiveChipUpdate()
  }

  onWindowResize() {
    this.scheduleProgressRingUpdate()
    this.scheduleActiveChipUpdate()
  }

  onLocationChange() {
    this.syncSectionVisibilityFromUrl()
    this.scheduleProgressRingUpdate()
    this.scheduleActiveChipUpdate()
  }

  getStickyStackOffsetPx() {
    let y = getStickyHeaderOffsetPx()
    const filterSection = document.querySelector('[data-collection-filters-sticky]')
    if (filterSection && !filterSection.hidden) {
      y += filterSection.offsetHeight
    }
    const ownSection = this.closest('.shopify-section')
    if (ownSection && !ownSection.hidden) {
      y += ownSection.offsetHeight
    }
    return y
  }

  isHiddenByFiltersOrSort(params) {
    const defaultSort = this.dataset.collectionDefaultSortBy ?? ''
    return isCollectionFiltersOrSortApplied(params, defaultSort)
  }

  syncSectionVisibilityFromUrl() {
    const hostSection = this.closest('.shopify-section')
    if (!hostSection) {
      return
    }
    const params = new URLSearchParams(window.location.search)
    hostSection.hidden = this.isHiddenByFiltersOrSort(params)
  }

  resolveChipTarget(link) {
    const productId = link.getAttribute('data-plp-scroll-target-product-id')?.trim()
    if (!productId) {
      return null
    }
    const byCardId = document.getElementById(`product-card-${productId}`)
    if (byCardId && isScrollTargetElementVisible(byCardId)) {
      return byCardId
    }
    const byTargetProductId = document.querySelector(
      `[data-target-product-id="${CSS.escape(productId)}"]`,
    )
    if (byTargetProductId && isScrollTargetElementVisible(byTargetProductId)) {
      return byTargetProductId
    }
    return null
  }

  scrollToChipTarget(link) {
    const target = this.resolveChipTarget(link)
    if (!target) {
      return
    }
    const offset = this.getStickyStackOffsetPx()
    const top = target.getBoundingClientRect().top + window.scrollY - offset
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' })
  }

  getPageScrollProgress() {
    const scroller = document.scrollingElement
    if (!scroller) {
      return 0
    }
    const maxY = scroller.scrollHeight - scroller.clientHeight
    if (maxY <= 0) {
      return 0
    }
    return Math.min(1, Math.max(0, scroller.scrollTop / maxY))
  }

  getRingLengthPixels(ring) {
    if (ring instanceof SVGCircleElement) {
      const n = typeof ring.getTotalLength === 'function' ? ring.getTotalLength() : 0
      if (Number.isFinite(n) && n > 0) {
        return n
      }
    }
    const r = Number.parseFloat(String(ring.getAttribute('r') ?? '16')) || 16
    return 2 * Math.PI * r
  }

  updateProgressRing() {
    this.progressRaf = null
    const progress = this.getPageScrollProgress()
    for (const ring of this.querySelectorAll('[data-plp-to-top-progress-ring]')) {
      if (!(ring instanceof SVGCircleElement)) {
        continue
      }
      const len = this.getRingLengthPixels(ring)
      if (!Number.isFinite(len) || len <= 0) {
        continue
      }
      ring.style.strokeDasharray = String(len)
      ring.style.strokeDashoffset = String(len * (1 - progress))
    }
  }

  scheduleProgressRingUpdate() {
    if (this.progressRaf != null) {
      return
    }
    this.progressRaf = requestAnimationFrame(() => this.updateProgressRing())
  }

  scrollActiveChipIntoView(activeLink) {
    if (!activeLink || window.innerWidth >= 990) {
      return
    }
    const chips = this.querySelector('[data-plp-anchor-chips]')
    if (!(chips instanceof HTMLElement)) {
      return
    }
    const chipsRect = chips.getBoundingClientRect()
    const linkRect = activeLink.getBoundingClientRect()
    /* One inset for left align + right margin: prefer resolved `padding-left` on `.chips`, else CSS token (px). */
    const chipScrollPadPx = Number.parseFloat(getComputedStyle(chips).paddingLeft)
    const trackInsetPx = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--plp-atelier-anchor-chip-track-inset').trim(),
    )
    const insetPx =
      Number.isFinite(chipScrollPadPx) && chipScrollPadPx > 0
        ? chipScrollPadPx
        : Number.isFinite(trackInsetPx) && trackInsetPx > 0
          ? trackInsetPx
          : 12
    const targetStartX = chipsRect.left + insetPx

    let nextLeft = null
    if (linkRect.left < targetStartX) {
      nextLeft = chips.scrollLeft + (linkRect.left - targetStartX)
    } else if (linkRect.right > chipsRect.right - insetPx) {
      // Align to chip start so long labels reveal their beginning, not the tail.
      nextLeft = chips.scrollLeft + (linkRect.left - targetStartX)
    }
    if (nextLeft == null) {
      return
    }

    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    chips.scrollTo({ left: Math.max(0, nextLeft), behavior: smooth ? 'smooth' : 'auto' })
  }

  updateActiveChip() {
    this.spyRaf = null
    const hostSection = this.closest('.shopify-section')
    if (hostSection?.hidden) {
      return
    }

    const links = [
      ...this.querySelectorAll(
        'a[data-plp-anchor-chip-link][data-plp-scroll-target-product-id]',
      ),
    ]
    if (links.length === 0) {
      return
    }

    const lineY = this.getStickyStackOffsetPx() + 8
    let activeLink = null
    let bestPastTop = -Infinity
    let bestFutureTop = Infinity
    let bestFutureLink = null

    for (const link of links) {
      const target = this.resolveChipTarget(link)
      if (!target) {
        continue
      }
      const top = target.getBoundingClientRect().top
      if (top <= lineY) {
        if (top > bestPastTop) {
          bestPastTop = top
          activeLink = link
        }
      } else if (top < bestFutureTop) {
        bestFutureTop = top
        bestFutureLink = link
      }
    }

    if (!activeLink) {
      activeLink = bestFutureLink ?? links.find((link) => this.resolveChipTarget(link)) ?? null
    }

    for (const link of links) {
      const on = activeLink != null && link === activeLink
      link.classList.toggle('plp-anchor-target-atelier__chip--active', on)
      if (on) {
        link.setAttribute('aria-current', 'true')
      } else {
        link.removeAttribute('aria-current')
      }
    }

    if (activeLink !== this.lastActiveLink) {
      this.scrollActiveChipIntoView(activeLink)
      this.lastActiveLink = activeLink
    }
  }

  scheduleActiveChipUpdate() {
    if (this.spyRaf != null) {
      return
    }
    this.spyRaf = requestAnimationFrame(() => this.updateActiveChip())
  }

  render() {
    return html`<slot></slot>`
  }
}

if (!customElements.get('plp-anchor-target-atelier')) {
  customElements.define('plp-anchor-target-atelier', PlpAnchorTargetAtelier)
}
